import React, {
  createElement, useContext, useEffect, useState,
} from 'react';
import {
  NavLink, useLocation, Link,
} from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Button,
  Drawer,
  useTheme,
  Typography,
  useMediaQuery,
  Chip,
  Box,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import type { NavLinkProps } from 'react-router-dom';

const NavLinkAction = React.forwardRef<HTMLAnchorElement, NavLinkProps & { showLabel?: boolean }>(
  ({ showLabel: _showLabel, ...props }, ref) => <NavLink ref={ref} {...props} />,
);
NavLinkAction.displayName = 'NavLinkAction';
import useNavLinks from 'shared/hooks/useNavLinks';
import { LOGOS } from 'shared/constants';
import variables from 'shared/styleVariables';
import { ROUTES } from 'shared/routes';
import UserContext from 'shared/contexts/UserContext';
import { Role } from 'API';
import { MH_ROLES } from 'services/auth';

const BottomNavChipSlot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showLabel' && prop !== 'selected' && prop !== 'value',
})({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledNavButton = styled(Button)<{ component?: React.ElementType; end?: boolean; to?: string }>(({ theme }) => ({
  borderRadius: 0,
  borderRightWidth: 4,
  borderStyle: 'solid',
  borderColor: 'transparent',
  boxShadow: 'none',
  marginBottom: '1.75rem',
  padding: '1rem 0.5rem',
  flexDirection: 'column',
  '@media (max-height: 849px)': {
    marginBottom: '0.5rem',
    padding: '0.5rem',
  },
  '& .MuiButton-startIcon': {
    color: theme.palette.text.secondary,
    margin: 0,
  },
  '&.active': {
    backgroundColor: theme.palette.grey5,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '& .nav-label-mini': { opacity: 1 },
    '& .MuiButton-startIcon': { color: theme.palette.primary.main },
  },
  '&.exact': {
    pointerEvents: 'none',
    '&:hover': { cursor: 'pointer' },
  },
  '&:hover .nav-label-mini': { opacity: 1 },
}));

const Nav: React.FC = () => {
  const initialPathRoot = window.location.pathname === '/'
    ? '/'
    : `/${window.location.pathname.split('/')[1]}`;
  const [pathRoot, setPathRoot] = useState(initialPathRoot);
  const links = useNavLinks();
  const theme = useTheme();
  const location = useLocation();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const [userState] = useContext(UserContext);
  const { role } = userState;
  let userType = role;

  if (role === Role.ADMIN || Role.TEAMMEMBER) {
    userType = 'MH';
  }

  if (role === Role.EMPLOYER) {
    userType = 'EM';
  }

  if (role === Role.FOREIGNNATIONAL) {
    userType = 'FN';
  }

  const handleChange = (event: React.ChangeEvent<{}>, newPathRoot: string) => {
    setPathRoot(newPathRoot);
  };

  const isActive = (path: string) => {
    if (path.length > 1 && location.pathname.indexOf(path) === 0) {
      return true;
    }

    // Edge case
    if (path.length > 1
      && path === '/people'
      && location.pathname.indexOf('/companies') === 0) {
      return true;
    }

    return false;
  };

  const isActiveExact = (path: string) => path === location.pathname;

  useEffect(() => {
    const newPath = window.location.pathname === '/'
      ? '/' : `/${window.location.pathname.split('/')[1]}`;

    if (pathRoot !== newPath) {
      setPathRoot(newPath);
    }
  }, [window.location.pathname]);

  const isTeamMember = MH_ROLES.includes(role);
  const queryParams = new URLSearchParams({ mine: isTeamMember ? 'true' : 'false' });
  const caseRoute = `${ROUTES.cases}?${queryParams.toString()}`;

  return (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={(t) => ({
        background: t.palette.common.white,
        gridColumn: 'nav-start / nav-end',
        gridRow: 'nav-start / nav-end',
        position: 'relative',
        zIndex: 100,
        [t.breakpoints.up('md')]: {
          paddingLeft: 0,
          paddingTop: `calc((${variables.headerHeight} - 4rem) / 2)`,
          width: variables.headerHeight,
          '& .MuiDrawer-paper': {
            paddingLeft: 0,
            paddingTop: `calc((${variables.headerHeight} - 4rem) / 2)`,
            width: variables.headerHeight,
          },
        },
      })}
    >
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <BottomNavigation
          sx={{ bottom: 0, position: 'fixed', width: '100%' }}
          value={pathRoot}
          onChange={handleChange}
          showLabels={links.length < 4}
        >
          {links
            .filter(
              // Five links won't fit on mobile, so drop settings at xs
              (link) => isSmUp || link.route !== ROUTES.admin,
            )
            .map((link) => (
              <BottomNavigationAction
                sx={(t) => ({
                  borderRadius: 0,
                  borderLeftWidth: 0,
                  borderTopWidth: 4,
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  boxShadow: 'none',
                  flexGrow: 0,
                  margin: '0 0.5rem',
                  paddingBottom: '0.5rem',
                  '&.active': {
                    backgroundColor: t.palette.grey5,
                    borderColor: t.palette.primary.main,
                    borderTopColor: t.palette.primary.main,
                    color: t.palette.primary.main,
                  },
                  '&.exact': {
                    pointerEvents: 'none',
                    '&:hover': { cursor: 'pointer' },
                  },
                })}
                className={`${isActive(link.route) ? 'active' : ''} ${isActiveExact(link.route) ? 'exact' : ''}`}
                component={NavLinkAction}
                end
                icon={createElement(link.icon)}
                key={link.label}
                label={link.label}
                to={link.label === 'Cases' ? caseRoute : link.route}
                value={link.route}
              />
            ))}
          <BottomNavChipSlot>
            <Chip
              label={userType}
              sx={{
                backgroundColor: alpha(variables.primary1, 0.25),
                color: variables.primary1,
                fontWeight: 700,
              }}
            />
          </BottomNavChipSlot>

        </BottomNavigation>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Drawer
          variant="permanent"
          open
          sx={{
            '&::-webkit-scrollbar': { display: 'none', width: 0, background: 'transparent' },
            '& .MuiDrawer-paper::-webkit-scrollbar': { display: 'none', width: 0, background: 'transparent' },
          }}
        >
          <figure>
            <Link
              style={{ display: 'block', marginBottom: '4rem' }}
              to={ROUTES.home}
            >
              <img
                src={LOGOS.voyager}
                alt=""
                style={{ display: 'block', height: theme.spacing(8), margin: '0 auto', width: theme.spacing(8) }}
              />
            </Link>
          </figure>

          {links
            .map((link) => (
              <StyledNavButton
                className={`${isActive(link.route) ? 'active' : ''} ${isActiveExact(link.route) ? 'exact' : ''}`}
                component={NavLink}
                end
                key={link.label}
                startIcon={createElement(link.icon, {
                  style: { height: '1.5em', width: '1.5em' },
                })}
                to={link.label === 'Cases' ? caseRoute : link.route}
              >
                <Typography
                  component="span"
                  className="nav-label-mini"
                  sx={(t) => ({
                    fontSize: '0.75rem',
                    fontWeight: 900,
                    opacity: 0,
                    transition: t.transitions.create(['opacity', 'max-height'], {
                      duration: t.transitions.duration.short,
                    }),
                  })}
                >
                  {link.label}
                </Typography>
              </StyledNavButton>
            ))}
          <Chip
            label={userType}
            sx={{
              marginTop: 'auto',
              marginBottom: '15px',
              marginInline: 'auto',
              backgroundColor: alpha(variables.primary1, 0.25),
              color: variables.primary1,
              fontWeight: 700,
            }}
          />
        </Drawer>
      </Box>
    </Box>
  );
};

export default Nav;
